import { useState, useRef, useCallback, useEffect } from 'react';
import { API_URL } from '../helpers/config';

export const useImageTour = (initialId = 1, displayAlert) => {
    // Map of id -> { url, pitch, yaw, hfov, id }  OR  { id, promise }
    const cache = useRef(new Map());
    
    const [currentScene, setCurrentScene] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentId, setCurrentId] = useState(Number(initialId) || 1);
    const [preloadedScenes, setPreloadedScenes] = useState([]);

    const updatePreloadedScenes = useCallback(() => {
        const scenes = Array.from(cache.current.values())
            .filter(item => item.url && !item.promise) // Solo los que ya están resueltos
            .map(item => ({...item}));
        setPreloadedScenes(scenes);
    }, []);

    const fetchImagePayload = async (rawId) => {
        const id = Number(rawId);

        if (cache.current.has(id)) {
            const cached = cache.current.get(id);
            if (cached.promise) {

                return cached.promise;
            }
            if (cached.url) {

                return cached;
            }
        }

        const fetchPromise = (async () => {
            try {
                const metadataRes = await fetch(`${API_URL}/image-metadata/${id}?t=${Date.now()}`, { 
                    credentials: 'include',
                    cache: 'no-cache'
                });
                if (!metadataRes.ok) throw new Error(`Metadata fetch failed for id ${id}`);
                const metadata = await metadataRes.json();

                const rot_x_val = parseFloat(metadata.rot_x);
                const rot_y_val = parseFloat(metadata.rot_y);
                const rot_z_val = parseFloat(metadata.rot_z);

                let calculatedPitch = (0.8847 * rot_x_val) + 1.0701;
                let calculatedYaw = (98.31 * rot_z_val) + 209.91;
                let calculatedHfov = (29.09 * rot_y_val) + 76.85;

                // Prevenir cuelgues de WebGL si la BBDD responde nulo
                if (isNaN(calculatedPitch)) calculatedPitch = 0;
                if (isNaN(calculatedYaw)) calculatedYaw = 0;
                if (isNaN(calculatedHfov)) calculatedHfov = 100;

                // Si ya tenemos una URL en el cache, no regeneramos el timestamp para evitar recargas infinitas en el motor
                const prefetchUrl = `${API_URL}/image-data/${id}?t=${Date.now()}`;

                const imageRes = await fetch(prefetchUrl, { 
                    credentials: 'include',
                    cache: 'no-cache' 
                });
                if (!imageRes.ok) {
                    console.error(`[useImageTour] HTTP Error fetch image data ${id}: status ${imageRes.status}`);
                    throw new Error(`Image data fetch failed for id ${id}`);
                }
                
                const imageUrl = prefetchUrl;

                const payload = {
                    id,
                    url: imageUrl,
                    pitch: calculatedPitch,
                    yaw: calculatedYaw,
                    hfov: calculatedHfov,
                    neighbors: metadata.neighbors || [],
                    isJunction: metadata.isJunction || false
                };
                
                cache.current.set(id, payload);
                updatePreloadedScenes();
                return payload;
            } catch (error) {
                cache.current.delete(id);
                throw error;
            }
        })();

        cache.current.set(id, { id, promise: fetchPromise });
        return fetchPromise;
    };

    const cleanupCache = (activeIds) => {
        const keysToDelete = [];
        // Limpieza agresiva: Mantener solo la ventana actual de precarga activa (-3 a +3 = 7 en total)
        // IMPORTANTE: Nunca borrar la escena que se está mostrando (currentId)
        const safeActiveIds = [...activeIds, currentId];

        if (cache.current.size > 7) {
            for (const [key, value] of cache.current.entries()) {
                if (!safeActiveIds.includes(key)) {
                    if (value.url && value.url.startsWith('blob:')) {
                        URL.revokeObjectURL(value.url);
                    }
                    keysToDelete.push(key);
                    // Dejar el caché en el límite sano (7)
                    if (cache.current.size - keysToDelete.length <= 7) break;
                }
            }
            if (keysToDelete.length > 0) {
                keysToDelete.forEach(key => cache.current.delete(key));
                updatePreloadedScenes();
            }
        }
    };


    const preloadAdjacent = useCallback((centerId, neighbors = []) => {
        const idsToKeep = [centerId];
        
        // Mantener la principal, 3 atrás y 3 adelante (7 en total sugerido)
        for (let i = -3; i <= 3; i++) {
            const id = centerId + i;
            if (id >= 1 && !idsToKeep.includes(id)) {
                idsToKeep.push(id);
            }
        }

        // Añadir vecinos del cruce/intersección
        neighbors.forEach(n => {
            if (n.id && !idsToKeep.includes(n.id)) {
                idsToKeep.push(n.id);
            }
        });

        // Limpiamos los blobs viejos de forma inmediata
        cleanupCache(idsToKeep);

        // Precarga secuencial en 2do plano retardada 500ms
        // Al usar await aseguramos descargar 1 a 1, sin saturar la red ni bloquear UI (Servicios)
        setTimeout(async () => {
            for (const id of idsToKeep) {
                if (id !== centerId && !cache.current.has(id)) {
                    try {
                        await fetchImagePayload(id);
                    } catch (e) {
                        // ignorar errores de precarga predictiva
                    }
                }
            }
        }, 500);

    }, []);

    const loadScene = useCallback(async (rawId, mode = 'silent') => {
        const id = Number(rawId); // <-- Forzar casteo a número EVITA CONCATENACIÓN en el preload (ej "6" + 0 = "60")
        console.log(`[Tour360] loadScene requested for Image ID: ${id}`);
        if (isLoading) {

             return;
        }
        

        if (mode !== 'silent') setIsLoading(true);
        
        try {
            setCurrentId(id);
            const payload = await fetchImagePayload(id);

            
            // Explicitly force React state update by copying object if needed
            setCurrentScene({...payload}); 

            
            // Precargar vecinas
            // Precargar vecinas
            preloadAdjacent(id, payload.neighbors || []);
            return true;
        } catch (error) {
            console.error("[useImageTour] Failed to load scene", error);
            if (mode !== 'silent') setIsLoading(false);
            return false;
        } finally {
            if (mode !== 'silent') {
                setTimeout(() => {

                    setIsLoading(false);
                }, 800);
                
                // Failsafe: asegurar que el loading se apague sí o sí tras 3 segundos
                setTimeout(() => { 

                    setIsLoading(false); 
                }, 3000);
            }
        }
    }, [preloadAdjacent, isLoading]);

    const handleSceneChange = useCallback(async (direction) => {
        // Bloqueo atómico: si ya está cargando, ignoramos el clic
        if (currentId === 0 || isLoading) return;
        
        const numCurrentId = Number(currentId);
        let newId = numCurrentId;
        // Según layout físico del cementerio: avanzar (next) significa reducir ID
        if (direction === 'next') {
            newId = numCurrentId - 1;
            if (newId < 1) newId = 1;
        } else if (direction === 'back') {
            newId = numCurrentId + 1;
        }

        if (newId !== numCurrentId) {

            await loadScene(newId, 'silent'); // <-- Fix: Pasar el string 'silent', no el boolean true
        }
    }, [currentId, loadScene, isLoading]);

    // Clean up all object urls when unmounting
    useEffect(() => {
        const currentCache = cache.current;
        return () => {
            for (const value of currentCache.values()) {
                if (value.url) URL.revokeObjectURL(value.url);
            }
            currentCache.clear();
        };
    }, []);

    return {
        currentScene,
        isLoading,
        currentId,
        loadScene,
        handleSceneChange,
        preloadedScenes
    };

};
